import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PatientSubcollection } from 'lib/documents';

import { getTimestampedDocId } from 'lib/utils/object';
import {
  handleError,
  handlePending,
  PageState,
  ThunkApi
} from 'lib/utils/redux';
import moment, { Moment } from 'moment';

import { SliderListItem } from 'components/_legacy/slider-list';
import { Repo } from 'lib/api/firebase';

import {
  DhiCategory,
  DhiCategoryMultipler
} from 'lib/documents/dizziness-handicap-scale';

import DhiJson from 'data/dizziness-handicap-scale.json';
import { createDhiRepo } from 'features/patient/_utils/patient-info/patientInfoSlice';
import { savePendingInventory } from 'features/patient/assessment/assessment-controller/questionnaire-control-flow-slice';
import { DhiQuestionnaire } from 'features/patient/_utils/questionnaires';
import { PostExecutionCallbacks } from '../../_components/questionnaire-paginator-types';
import { controlflowSubmissionEventHandler } from '../../assessment-controller/_utils/control-flow-submission-eventhandler';
import { PcsInventory } from '../pcs/pcsSlice';

const dhiOrder = DhiJson.questions.map(q => q.title);
const questionnaire = DhiQuestionnaire;

const dhiSubsection = questionnaire.subsections['dhi'];

// eslint-disable-next-line
export interface DhiInventory extends PcsInventory {
  type: string;
  data: Record<DhiCategory, number> & Record<string, number>;
}

export interface DhiState extends PageState {
  dhi: DhiInventory;
  invalidItems: string[];
  isSubmitting: boolean;
  lastUpdated?: Moment;
  showModal: boolean;
  /**
   * A map of question-to-categories
   */
  categories: Record<string, string[]>;
}

/**
 * @deprecated
 */
export const categories = [
  'Total',
  'Physical',
  'Emotional',
  'Functional'
] as const;
const notSelected = -1;

export function createNewDhi(): DhiInventory {
  return {
    date: moment(),
    data: dhiSubsection.questions.reduce(
      (acc: Record<string, number>, question) => {
        acc[question.title] = notSelected;
        return acc;
      },
      {}
    ) as any,
    instructions: dhiSubsection.instructions,
    min: dhiSubsection.min,
    max: dhiSubsection.max,
    description: dhiSubsection.description,
    id: dhiSubsection.id,
    type: dhiSubsection.type,
    beforeLabel: dhiSubsection.beforeLabel,
    afterLabel: dhiSubsection.afterLabel,
    colorsForward: dhiSubsection.colorsForward ?? true,
    iconsForward: dhiSubsection.iconsForward ?? true
  };
}

function createCategories(): Record<string, string[]> {
  return questionnaire.subsections['dhi'].questions.reduce(
    (acc: Record<string, string[]>, question) => {
      acc[question.title] = question.categories;
      return acc;
    },
    {}
  );
}

const initialState: DhiState = {
  hasError: false,
  dhi: createNewDhi(),
  invalidItems: [],
  isLoading: false,
  isSubmitting: false,
  showModal: false,
  categories: createCategories()
};

let dhiRepo: Repo<DhiInventory>;
const initialLoad = createAsyncThunk(
  'dhi/initialLoad',
  async (patientId: string) => {
    dhiRepo = createDhiRepo(patientId);

    return await dhiRepo.first({
      orderBy: ['date', 'desc']
    });
  }
);

const submit = createAsyncThunk<
  boolean,
  PostExecutionCallbacks,
  ThunkApi<DhiState>
>('dhi/submit', async (callbacks, { getState, dispatch }) => {
  return await controlflowSubmissionEventHandler(callbacks, async () => {
    dispatch(dhiSlice.actions.validate());

    const { invalidItems } = getState();
    if (invalidItems.length) return false;

    /**
     * WARNING: Race condition
     */
    // await dispatch(dhiSlice.actions.filterMetaProperties())
    dispatch(dhiSlice.actions.calculateCategories());
    dispatch(dhiSlice.actions.calculateCategoryMultipliers());

    dispatch(dhiSlice.actions.setDhiDate());

    const { dhi } = getState();
    await savePendingInventory(PatientSubcollection.InventoriesDhi, dhi);
    return true;
  });
});

const dhiSlice = createSlice({
  name: 'dhi',
  initialState,
  reducers: {
    setDhiDate: state => {
      const now = moment();
      state.dhi.id = getTimestampedDocId(now);
      state.dhi.date = now;
    },
    updateDhiItem: (state, { payload }: PayloadAction<SliderListItem>) => {
      const { title, value } = payload;
      state.invalidItems = state.invalidItems.filter(i => i !== title);
      state.dhi.data[title] = value;
    },
    closeModal: state => {
      state.showModal = false;
    },
    validate: state => {
      state.invalidItems = Object.entries(state.dhi.data)
        .filter(([, value]) => value === notSelected)
        .map(([key]) => key);
    },
    calculateCategories: state => {
      Object.entries(state.dhi.data).forEach(([questionTitle, value]) => {
        // Exit if the question is not tracked
        if (!state.categories[questionTitle]) return;

        for (const category of state.categories[questionTitle]) {
          /**
           * Get the category, or initialise to 0
           */
          const currentValue = state.dhi.data[category] ?? 0;
          state.dhi.data[category] = currentValue + value;
        }
      });
    },
    calculateCategoryMultipliers: state => {
      for (const category of Object.values(DhiCategory)) {
        state.dhi.data[category] *= DhiCategoryMultipler[category];
      }
    }
    // ,
    // filterMetaProperties: state => {
    //   const neededProperties = ["data", "date"];

    //   for (const category of Object.values(state.dhi)) {
    //     if (neededProperties.indexOf(category) == -1) {
    //       state.dhi
    //     }
    //   }
    // }
  },
  extraReducers: builder => {
    builder.addCase(initialLoad.pending, handlePending);
    builder.addCase(initialLoad.rejected, handleError);
    builder.addCase(initialLoad.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.lastUpdated = payload ? payload.date : undefined;
    });

    builder.addCase(submit.pending, state => {
      state.isSubmitting = true;
    });
    builder.addCase(submit.rejected, handleError);
    builder.addCase(submit.fulfilled, (state, { payload: isSaved }) => {
      state.isSubmitting = false;
      if (isSaved) {
        state.showModal = isSaved;
        state.lastUpdated = moment().utc(true);
        state.dhi = createNewDhi();
      }
    });
  }
});

export {
  createCategories,
  dhiOrder,
  initialLoad,
  initialState,
  notSelected,
  submit
};
export const { updateDhiItem, setDhiDate, closeModal, validate } =
  dhiSlice.actions;
export const DhiSliceReducer = dhiSlice.reducer;
export default dhiSlice.reducer;
